import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  type SxProps,
  type Theme,
} from "@mui/material";
import { memo, useCallback, useMemo, type ChangeEvent } from "react";
import { type Edi214ShipmentStatus } from "../../../generated/graphql";
import {
  EDI_214_GROUPS,
  ENGINE_ONLY_NOTE,
  selectedRetired214Group,
  toggleEdi214Status,
  type Edi214Group,
  type Edi214Option,
} from "../edi-214-groups";
import { PARTNERSHIP_FIELD_COPY } from "../tenedi-connection-copy";

const styles = {
  groups: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 2,
    mt: 1,
  } satisfies SxProps<Theme>,
  note: { mt: -0.5, ml: 4 } satisfies SxProps<Theme>,
};

type StatusCheckboxProps = {
  readonly option: Edi214Option;
  readonly checked: boolean;
  readonly disabled: boolean;
  readonly onToggle: (status: Edi214ShipmentStatus, checked: boolean) => void;
};

const StatusCheckbox = memo(
  ({ option, checked, disabled, onToggle }: StatusCheckboxProps) => {
    const handleChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        onToggle(option.status, event.target.checked);
      },
      [onToggle, option.status],
    );
    return (
      <>
        <FormControlLabel
          control={
            <Checkbox
              checked={checked}
              disabled={disabled}
              onChange={handleChange}
            />
          }
          label={option.label}
        />
        {option.engineOnly && (
          <FormHelperText sx={styles.note}>{ENGINE_ONLY_NOTE}</FormHelperText>
        )}
      </>
    );
  },
);
StatusCheckbox.displayName = "StatusCheckbox";

type Edi214CheckboxListProps = {
  readonly value: readonly Edi214ShipmentStatus[];
  readonly disabled: boolean;
  readonly error: string | undefined;
  readonly onChange: (value: Edi214ShipmentStatus[]) => void;
};

/**
 * The 214 subscriptions as grouped checkboxes (plan section 2.4). Retired
 * statuses the partnership still has appear in one more group, ticked, so the
 * operator can untick them and save.
 */
const Edi214CheckboxList = ({
  value,
  disabled,
  error,
  onChange,
}: Edi214CheckboxListProps) => {
  const selected = useMemo(() => new Set(value), [value]);
  const groups = useMemo<readonly Edi214Group[]>(() => {
    const retired = selectedRetired214Group(value);
    return retired === null ? EDI_214_GROUPS : [...EDI_214_GROUPS, retired];
  }, [value]);
  const handleToggle = useCallback(
    (status: Edi214ShipmentStatus, checked: boolean) => {
      onChange(toggleEdi214Status(value, status, checked));
    },
    [onChange, value],
  );

  return (
    <Box>
      <FormLabel>
        {PARTNERSHIP_FIELD_COPY.edi214ShipmentStatuses.label}
      </FormLabel>
      <FormHelperText error={error !== undefined}>
        {error ?? PARTNERSHIP_FIELD_COPY.edi214ShipmentStatuses.helper}
      </FormHelperText>
      <Box sx={styles.groups}>
        {groups.map((group) => (
          <FormControl key={group.title} component="fieldset">
            <FormLabel component="legend">{group.title}</FormLabel>
            <FormGroup>
              {group.options.map((option) => (
                <StatusCheckbox
                  key={option.status}
                  checked={selected.has(option.status)}
                  disabled={disabled}
                  option={option}
                  onToggle={handleToggle}
                />
              ))}
            </FormGroup>
          </FormControl>
        ))}
      </Box>
    </Box>
  );
};

export default Edi214CheckboxList;
